import { useState, useEffect, useCallback, useRef } from "react";
import * as Y from "yjs";
import { fromUint8Array, toUint8Array } from "js-base64";
import { invoke } from "../lib/bridge";

interface UseYDocResult {
  doc: Y.Doc | null;
  loading: boolean;
  error: string | null;
}

export function useYDoc(docId: string, syncVersion?: number): UseYDocResult {
  const [doc, setDoc] = useState<Y.Doc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, forceUpdate] = useState(0);
  // Read-only reference used only by the syncVersion effect; the main effect
  // is the sole owner of the doc lifecycle.
  const docRef = useRef<Y.Doc | null>(null);

  const triggerUpdate = useCallback(() => {
    forceUpdate((n) => n + 1);
  }, []);

  // Single effect owns the full doc lifecycle: create, replay journal, register
  // listeners, and tear everything down in cleanup. Named handler references are
  // captured so cleanup can call doc.off() for each one, preventing listener
  // accumulation across StrictMode remounts or docId changes.
  useEffect(() => {
    let cancelled = false;

    // Named handlers so cleanup can call doc.off() with the exact same reference.
    const saveHandler = async (update: Uint8Array, origin: unknown) => {
      if (origin === "load") return;
      const b64 = fromUint8Array(update);
      try {
        await invoke("save_update", { docId, updateB64: b64 });
      } catch (e) {
        console.error("Failed to save update:", e);
      }
    };
    const renderHandler = () => triggerUpdate();

    setLoading(true);
    setError(null);
    setDoc(null);

    async function init() {
      try {
        const newDoc = new Y.Doc();

        // Load existing local updates. Remote updates are pulled by App.tsx's
        // global sync poll (the sole owner of pull_sync_updates); they land in
        // the local journal and are picked up here and on syncVersion changes.
        const updates = await invoke<string[]>("load_updates", { docId });
        if (cancelled) { newDoc.destroy(); return; }
        for (const b64 of updates) {
          Y.applyUpdate(newDoc, toUint8Array(b64), "load");
        }

        // Journal grew large: fold it into a snapshot in the background so future
        // opens/replays stay cheap. The bridge re-checks the threshold authoritatively.
        if (updates.length > 200) {
          invoke("compact_document", { docId }).catch(() => {});
        }

        // Register named handlers so cleanup can remove them with off().
        newDoc.on("update", saveHandler);
        newDoc.on("update", renderHandler);

        docRef.current = newDoc;
        setDoc(newDoc);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(`${e}`);
          setLoading(false);
        }
        console.error("useYDoc init failed:", e);
      }
    }

    init();

    return () => {
      cancelled = true;
      const d = docRef.current;
      if (d) {
        // Remove listeners before destroying so no stale callbacks fire.
        d.off("update", saveHandler);
        d.off("update", renderHandler);
        d.destroy();
        docRef.current = null;
      }
      setDoc(null);
    };
  }, [docId, triggerUpdate]);

  // Re-apply from the local journal when syncVersion changes. App.tsx's global
  // poll has already pulled remote updates into local SQLite and bumped
  // syncVersion; re-applying known updates is a no-op in Yjs, new ones merge in.
  useEffect(() => {
    if (syncVersion === undefined || !docRef.current || docRef.current.isDestroyed) return;

    (async () => {
      try {
        const updates = await invoke<string[]>("load_updates", { docId });
        for (const b64 of updates) {
          if (docRef.current && !docRef.current.isDestroyed) {
            Y.applyUpdate(docRef.current, toUint8Array(b64), "load");
          }
        }
      } catch {
        // offline or not yet loaded
      }
    })();
  }, [syncVersion, docId]);

  return { doc, loading, error };
}
